/**
 * MascotaDetailPage - Detalle de una mascota (solo lectura).
 * Muestra plan afiliado, próximo cobro y permite cancelar/reactivar suscripción.
 */

import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Camera } from 'lucide-react';
import { usePet, useUploadPetPhoto } from '@modules/pets/presentation/hooks/usePets';
import {
  useCancelSubscription,
  useReactivateSubscription,
} from '@modules/billing/presentation/hooks/useBilling';
import { getErrorMessage } from '@shared/infrastructure/http/api.error';
import { cn } from '@shared/utils/cn';

// Referencia solo para mostrar al usuario; el cobro real lo define el backend.
const UF_CLP_APPROX = 37000;

const SPECIES_EMOJI: Record<string, string> = {
  DOG: '🐶', CAT: '🐱', perro: '🐶', gato: '🐱',
};

function speciesEmoji(species?: string) {
  if (!species) return '🐾';
  return SPECIES_EMOJI[species] ?? SPECIES_EMOJI[species.toLowerCase()] ?? '🐾';
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' });
}

function statusPill(status?: string): { label: string; className: string } {
  switch ((status ?? '').toUpperCase()) {
    case 'ACTIVE':
      return { label: 'Activo', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'PENDING':
      return { label: 'Pago pendiente', className: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'PAST_DUE':
      return { label: 'Pago vencido', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    case 'SUSPENDED':
      return { label: 'Suspendido', className: 'bg-violet-50 text-violet-700 border-violet-200' };
    case 'CANCELED':
    case 'CANCELLED':
      return { label: 'Cancelado', className: 'bg-red-50 text-red-600 border-red-200' };
    default:
      return { label: status ?? 'Sin plan', className: 'bg-gray-100 text-gray-500 border-gray-200' };
  }
}

export function MascotaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = usePet(id);
  const pet = data?.pet;
  const subscription = data?.subscription;

  const cancelMutation = useCancelSubscription();
  const reactivateMutation = useReactivateSubscription();
  const photoMutation = useUploadPetPhoto(id ?? '');

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const status = (subscription?.status ?? '').toUpperCase();
  const isOpen = ['ACTIVE', 'PAST_DUE', 'PENDING'].includes(status);
  const isReactivable = status === 'CANCELED' || status === 'CANCELLED' || status === 'SUSPENDED';
  const isActive = status === 'ACTIVE';
  const isCanceled = status === 'CANCELED' || status === 'CANCELLED';
  /** Cancelada pero con cobertura vigente (remanente) hasta nextBillingDate. */
  const hasRemanente = isCanceled && pet?.hasOpenSubscription === true;

  const nextPaymentUf = subscription?.finalPriceUf ?? 0;
  const nextPaymentClp = Math.round(nextPaymentUf * UF_CLP_APPROX);
  const pill = hasRemanente
    ? { label: 'Cancelado · cobertura vigente', className: 'bg-amber-50 text-amber-700 border-amber-200' }
    : statusPill(subscription?.status);

  const handleCancel = async () => {
    if (!subscription) return;
    try {
      await cancelMutation.mutateAsync({
        id: subscription.id,
        reason: cancelReason.trim() || undefined,
      });
      toast.success(
        'Suscripción cancelada. Sigues cubierto hasta el fin del período pagado; no se cobrará el próximo mes.',
      );
      setCancelOpen(false);
      setCancelReason('');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleReactivate = async () => {
    if (!subscription) return;
    try {
      await reactivateMutation.mutateAsync(subscription.id);
      toast.success('Plan reactivado y cobrado. ¡Listo!');
      setReactivateOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('El archivo debe ser una imagen');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar 5 MB');
      e.target.value = '';
      return;
    }
    try {
      await photoMutation.mutateAsync(file);
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      e.target.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFDCE6]">
        <div className="animate-pulse text-gray-600 text-sm">Cargando mascota...</div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#FFDCE6] px-4">
        <p className="text-red-700 text-sm">No se pudo cargar la mascota.</p>
        <Button asChild variant="outline">
          <Link to="/mascotas">Volver a Mis Mascotas</Link>
        </Button>
      </div>
    );
  }

  const hasHealthNotes =
    pet.isSterilized || pet.disease || pet.minorDisease || pet.restricted;
  const preConditions = pet.preExistingConditions ?? [];

  return (
    <div className="min-h-screen flex flex-col bg-[#FFDCE6]">
      <section className="px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-6 md:p-10 shadow-lg space-y-8">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                <Link to="/sucursal-virtual" className="text-gray-500 hover:text-[#FF6F61]">
                  Mi cuenta
                </Link>
                <span className="text-gray-300">/</span>
                <Link to="/mascotas" className="text-gray-500 hover:text-[#FF6F61]">
                  Mis Mascotas
                </Link>
                <span className="text-gray-300">/</span>
                <span className="text-gray-800 font-medium truncate max-w-[180px]">{pet.name}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
                ← Volver
              </Button>
            </div>

            {/* Identidad */}
            <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
              <div className="relative shrink-0">
                <div className="w-28 h-28 rounded-2xl bg-[#FFDCE6]/60 flex items-center justify-center overflow-hidden">
                  {pet.photoUrl ? (
                    <img src={pet.photoUrl} alt={pet.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-5xl select-none">{speciesEmoji(pet.species)}</span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoChange}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoMutation.isPending}
                  className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full bg-[#FF6F61] text-white shadow-md hover:bg-[#FF6F61]/90 flex items-center justify-center disabled:opacity-60"
                  aria-label="Cambiar foto"
                  title="Cambiar foto"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>

              <div className="text-center sm:text-left flex-1 min-w-0">
                <h1 className="text-3xl font-bold text-gray-900">{pet.name}</h1>
                <p className="text-sm text-gray-500 mt-1">
                  {pet.species === 'DOG' ? 'Perro' : pet.species === 'CAT' ? 'Gato' : pet.species}
                  {pet.breed ? ` · ${pet.breed}` : ''}
                  {pet.age != null ? ` · ${pet.age} ${pet.age === 1 ? 'año' : 'años'}` : ''}
                </p>
                <p className="text-xs text-gray-400 mt-1">Nació el {formatDate(pet.birthDate)}</p>
                <span
                  className={cn(
                    'inline-flex items-center mt-3 text-xs font-medium px-2.5 py-1 rounded-full border',
                    pill.className,
                  )}
                >
                  {pill.label}
                </span>
              </div>
            </div>

            {/* Información de la mascota (solo salud) */}
            {(hasHealthNotes || preConditions.length > 0) && (
              <div className="rounded-2xl border border-gray-200 bg-gray-50/70 px-5 py-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
                  Información de la mascota
                </p>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <Row label="Esterilizada/o" value={pet.isSterilized ? 'Sí' : 'No'} />
                  <Row label="Enf. graves" value={pet.disease ? 'Sí' : 'No'} />
                  <Row label="Enf. menores" value={pet.minorDisease ? 'Sí' : 'No'} />
                  <Row label="Restricciones" value={pet.restricted ? 'Sí' : 'No'} />
                  {preConditions.length > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-xs text-gray-500">Condiciones preexistentes</dt>
                      <dd className="text-sm text-gray-900 mt-0.5">{preConditions.join(', ')}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Plan afiliado / próximo cobro */}
            {subscription ? (
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#FF6F61]">
                      Plan afiliado
                    </p>
                    <p className="font-semibold text-gray-900 mt-0.5">
                      {subscription.planName ?? 'Plan'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 font-mono break-all">
                      Plan ID: {subscription.planId}
                    </p>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/planes/${subscription.planId}`}>Ver detalles del plan</Link>
                  </Button>
                </div>

                {/* Remanente: cancelada pero cobertura vigente hasta nextBillingDate */}
                {hasRemanente && (
                  <div className="px-5 py-3 bg-emerald-50/80 border-b border-emerald-100 text-sm text-emerald-900 leading-relaxed">
                    Suscripción <strong>cancelada</strong>, pero{' '}
                    <strong>sigues usando el plan</strong> hasta el{' '}
                    <strong>{formatDate(subscription.nextBillingDate)}</strong>. Después de esa
                    fecha, la cobertura dejará de estar activa.
                  </div>
                )}

                <dl className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">
                      {isActive
                        ? 'Próximo cobro estimado'
                        : hasRemanente
                          ? 'Cobertura activa hasta'
                          : 'Próxima fecha (ref.)'}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {formatDate(subscription.nextBillingDate)}
                    </dd>
                  </div>
                  {!hasRemanente && (
                    <div className="sm:col-span-2 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 flex items-end justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Monto estimado del próximo cobro</p>
                        <p className="text-lg font-semibold text-gray-900 tabular-nums">
                          {nextPaymentUf.toFixed(2)} UF{' '}
                          <span className="text-gray-500 font-normal text-xs">
                            (~{nextPaymentClp.toLocaleString('es-CL')} CLP)
                          </span>
                        </p>
                      </div>
                    </div>
                  )}
                  {hasRemanente && (
                    <div className="sm:col-span-2 rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 text-xs text-gray-600">
                      No hay próximos cobros. Puedes reactivar el plan en cualquier momento antes
                      o después del corte.
                    </div>
                  )}
                </dl>

                {!isOpen && isReactivable && !hasRemanente && (
                  <div className="px-5 py-3 bg-amber-50/70 border-t border-amber-100 text-xs text-amber-900 leading-relaxed">
                    Esta suscripción está{' '}
                    <strong className="font-semibold">{pill.label.toLowerCase()}</strong>. Al
                    reactivarla se cobra el primer mes inmediatamente con tu tarjeta principal.
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-6 text-center space-y-3">
                <p className="text-sm text-gray-600">Esta mascota aún no tiene un plan contratado.</p>
                <Button
                  className="bg-[#FF6F61] text-white"
                  onClick={() => navigate('/contratacion')}
                >
                  Contratar plan
                </Button>
              </div>
            )}

            {/* Acciones */}
            {subscription && (
              <div className="flex flex-wrap gap-3">
                {isOpen && (
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => setCancelOpen(true)}
                  >
                    Cancelar suscripción
                  </Button>
                )}
                {isReactivable && (
                  <Button
                    className="bg-[#FF6F61] text-white"
                    onClick={() => setReactivateOpen(true)}
                  >
                    Reactivar plan
                  </Button>
                )}
                <Button variant="outline" asChild>
                  <Link to="/mascotas">Volver a mis mascotas</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cancelar */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar suscripción</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                Vas a cancelar el plan <strong>{subscription?.planName ?? 'actual'}</strong> de{' '}
                <strong>{pet.name}</strong>.
              </span>
              <span className="block text-gray-700 bg-emerald-50/70 border border-emerald-100 rounded-lg p-3 text-sm">
                Si ya pagaste el mes en curso, <strong>seguirás usando el plan</strong> hasta el{' '}
                {formatDate(subscription?.nextBillingDate)}. Después de esa fecha{' '}
                <strong>no se cobrará</strong> el siguiente mes.
              </span>
              <span className="block text-xs text-gray-500">
                Si cambias de idea antes del corte, puedes reactivarlo cuando quieras desde esta
                misma página.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label htmlFor="reason" className="text-xs text-gray-600">
              Motivo (opcional)
            </label>
            <Textarea
              id="reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Nos ayuda a mejorar el servicio"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Mantener plan
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={cancelMutation.isPending}
              onClick={handleCancel}
            >
              {cancelMutation.isPending ? 'Cancelando...' : 'Sí, cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivar */}
      <Dialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivar plan</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                Vas a reactivar el plan <strong>{subscription?.planName ?? 'anterior'}</strong> de{' '}
                <strong>{pet.name}</strong>.
              </span>
              {hasRemanente ? (
                <span className="block text-gray-700 bg-emerald-50/80 border border-emerald-100 rounded-lg p-3 text-sm">
                  Aún estás dentro del período pagado (hasta{' '}
                  <strong>{formatDate(subscription?.nextBillingDate)}</strong>). La reactivación
                  mantiene tu cobertura y se retoma el cobro en el próximo ciclo.
                </span>
              ) : (
                <span className="block text-gray-700 bg-amber-50/70 border border-amber-100 rounded-lg p-3 text-sm">
                  Se hará un <strong>cobro inmediato</strong> del primer mes contra tu tarjeta
                  principal. Si no tienes tarjeta registrada, agrégala antes en{' '}
                  <Link to="/sucursal-virtual" className="text-[#FF6F61] underline">
                    Mi cuenta
                  </Link>
                  .
                </span>
              )}
              {!hasRemanente && nextPaymentUf > 0 && (
                <span className="block text-sm">
                  Monto: <strong>{nextPaymentUf.toFixed(2)} UF</strong>{' '}
                  <span className="text-gray-500">
                    (~{nextPaymentClp.toLocaleString('es-CL')} CLP)
                  </span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#FF6F61] text-white"
              disabled={reactivateMutation.isPending}
              onClick={handleReactivate}
            >
              {reactivateMutation.isPending
                ? 'Reactivando...'
                : hasRemanente
                  ? 'Reactivar plan'
                  : 'Reactivar y pagar ahora'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 mt-0.5">{value}</dd>
    </div>
  );
}
